import React from "react";
import {
  Box,
  Checkbox,
  Radio,
  Typography,
  createStyles,
  withStyles
} from "@material-ui/core";
import CheckIcon from "@material-ui/icons/Check";
import { magicLinkColors, magicLinkRadius } from "./magicLinkTokens";

interface Props {
  label: React.ReactNode;
  selected: boolean;
  multiple?: boolean;
  onSelect: () => void;
  className?: string;
  inputId?: string;
  disabled?: boolean;
  classes: Record<string, string>;
}

const styles = (theme: any) =>
  createStyles({
    row: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      minHeight: 40,
      boxSizing: "border-box",
      padding: "0 12px",
      borderRadius: magicLinkRadius.card,
      border: `1px solid ${magicLinkColors.border}`,
      backgroundColor: magicLinkColors.option,
      cursor: "pointer",
      userSelect: "none",
      transition: "border-color 150ms ease, background-color 150ms ease",
      "&:hover": {
        borderColor: theme.palette.primary.main
      },
      "&:focus-visible": {
        outline: `2px solid ${theme.palette.primary.main}`,
        outlineOffset: 2
      }
    },
    disabled: {
      cursor: "default",
      opacity: 0.5,
      "&:hover": {
        borderColor: magicLinkColors.border
      }
    },
    selected: {
      borderColor: theme.palette.primary.main,
      backgroundColor: magicLinkColors.optionActive
    },
    control: {
      padding: 0,
      "& svg": {
        fontSize: 18
      }
    },
    checkboxIcon: {
      width: 18,
      height: 18,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      "&::before": {
        content: '""',
        width: 14,
        height: 14,
        boxSizing: "border-box",
        borderRadius: 2,
        border: `2px solid ${magicLinkColors.textMuted}`,
        backgroundColor: magicLinkColors.surface
      }
    },
    checkboxIconChecked: {
      "&::before": {
        display: "none"
      }
    },
    checkboxBox: {
      width: 14,
      height: 14,
      borderRadius: 2,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: theme.palette.primary.main,
      color: magicLinkColors.surface
    },
    radioIcon: {
      width: 16,
      height: 16,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      "&::before": {
        content: '""',
        width: 13,
        height: 13,
        boxSizing: "border-box",
        borderRadius: "50%",
        border: `1px solid ${magicLinkColors.text}`,
        backgroundColor: magicLinkColors.surface
      }
    },
    radioIconChecked: {
      position: "relative",
      "&::before": {
        borderColor: theme.palette.primary.main
      },
      "&::after": {
        content: '""',
        position: "absolute",
        width: 9,
        height: 9,
        borderRadius: "50%",
        backgroundColor: theme.palette.primary.main
      }
    },
    label: {
      fontFamily: "Nunito Sans",
      fontSize: "12px",
      fontWeight: 400,
      lineHeight: "16px",
      color: magicLinkColors.text,
      padding: "12px 0"
    },
    labelSelected: {
      color: magicLinkColors.textStrong
    }
  });

const OptionRow = ({
  label,
  selected,
  multiple,
  onSelect,
  className,
  inputId,
  disabled,
  classes
}: Props) => {
  const select = () => {
    if (!disabled) {
      onSelect();
    }
  };
  const controlProps = {
    checked: selected,
    color: "primary" as const,
    tabIndex: -1,
    disableRipple: true,
    disabled,
    onClick: (event: React.MouseEvent) => event.stopPropagation(),
    onChange: select,
    inputProps: { "aria-hidden": true, id: inputId }
  };
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      select();
    }
  };
  return (
    <Box
      role={multiple ? "checkbox" : "radio"}
      aria-checked={selected}
      aria-disabled={disabled}
      tabIndex={disabled ? -1 : 0}
      onClick={select}
      onKeyDown={handleKeyDown}
      className={[
        classes.row,
        selected && classes.selected,
        disabled && classes.disabled,
        className
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {multiple ? (
        <Checkbox
          {...controlProps}
          className={classes.control}
          icon={<span className={classes.checkboxIcon} />}
          checkedIcon={
            <span className={`${classes.checkboxIcon} ${classes.checkboxIconChecked}`}>
              <span className={classes.checkboxBox}>
                <CheckIcon style={{ fontSize: 12 }} />
              </span>
            </span>
          }
        />
      ) : (
        <Radio
          {...controlProps}
          className={classes.control}
          icon={<span className={classes.radioIcon} />}
          checkedIcon={
            <span className={`${classes.radioIcon} ${classes.radioIconChecked}`} />
          }
        />
      )}
      <Typography
        className={
          selected ? `${classes.label} ${classes.labelSelected}` : classes.label
        }
      >
        {label}
      </Typography>
    </Box>
  );
};

export default withStyles(styles)(OptionRow);
